const content = `<p>One of the fundamental concepts of software design, if not THE fundamental concept, is <strong>coupling</strong>.</p>

<p>Tight coupling makes code difficult to change. And as ease of change is the goal of software design, low coupling is what you want to achieve.</p>

<p>What is coupling though?</p>

<h2 id="What-coupling-is">What coupling is</h2>

<p>Two pieces of code are coupled together when one knows about the other and may need to be updated when the other changes.</p>

<p>To understand what knowing about another piece of code means, let's take an example to illustrate. Consider this function that takes a first name and a last name, and that formats them into a full name:</p>

<pre><code class="language-ruby">def format_full_name(first_name, last_name)
&nbsp; first_name + ' ' + last_name
end</code></pre>

<p>So far so good. Now let's consider another function in the same codebase, that crafts the contents of an email to a customer:</p>

<pre><code class="language-ruby">def compose_email_to(full_name)
&nbsp; first_name, _last_name = full_name.split(' ')
&nbsp; "Hey #{first_name}, what's up?"
end</code></pre>

<p>It receives a formatted full name, but it needs the first name to start writing the email. So it extracts the first name from the full name.</p>

<p>What's wrong with this piece of code?</p>

<p>The problem with this code is that&nbsp;<code>compose_email_to</code>&nbsp;knows how&nbsp;<code>format_full_name</code>&nbsp;has formatted the full name. It knows that the full name is the first name and the last name separated by a space. That's how it can do the reverse operation to obtain the first name.</p>

<p>Since&nbsp;<code>compose_email_to</code>&nbsp;knows this about&nbsp;<code>format_full_name,</code>&nbsp;by our definition of coupling it means that there is coupling between them.</p>

<h2 id="Why-coupling-is-bad-design">Why coupling is bad design</h2>

<p><br>This kind of coupling is bad design because it makes the code difficult to change. How so?</p>

<p>Imagine we want to change <code>format_full_name</code> and decide that the right way to format full names should be this:</p>

<pre><code class="language-ruby">def format_full_name(first_name, last_name)
  last_name.upcase + ', ' + first_name
end</code></pre>

<p>Then <code>compose_email_to</code> is silently broken. Indeed, the following line in <code>compose_email_to</code> now returns nonsensical results:</p>

<pre><code class="language-ruby">first_name, _last_name = full_name.split(' ')</code></pre>

<p>For our program to continue working, we have to remember all places in the codebase that are coupled with the code that we change, and go about and change them. This is complicated and error prone.</p>

<p>The more coupling, the more difficult it becomes to change the code.</p>

<h2 id="How-to-fix">How to fix</h2>

<p>In this case, if you can access the first name of the email recipient then the problem disappears:</p>

<pre><code class="language-ruby">def compose_email_to(recipient)
  "Hey #{recipient.first_name}, what's up?"
end</code></pre>

<p>But for the sake of the exercise let's assume that you can't change the interface of <code>compose_email_to</code>. Then one way to improve the code is to increase the locality of coupling, which means moving the two coupled pieces of code to be close together:</p>

<pre><code class="language-ruby">def format_full_name(first_name, last_name)
  first_name + ' ' + last_name
end

def first_name_from_full_name(full_name)
  full_name.split(' ').first
end</code></pre>

<p>Then&nbsp;<code>compose_email_to</code>&nbsp;can just use <code>first_name_from_full_name</code>. And if you need to change how full names are formatted, the code to update is right next to it, so it's a smaller effort to remember to change it.</p>

<p>It’s best if you can remove coupling completely. But if you can't, or if it’s too difficult, you can still improve the code by making the coupled parts close from one another.</p>

<p>This particular example could be nicely complemented by a test that makes sure the two functions stay in line:</p>

<pre><code class="language-ruby">def test_extract_first_name_from_full_name
  assert_equal first_name_from_full_name(format_full_name('John', 'Doe')), 'John'
end</code></pre>

<p>This would be a good case for <a href="https://medium.com/criteo-engineering/introduction-to-property-based-testing-f5236229d237">property-based testing</a>.</p>

<h2 id="There-is-(way)-more-to-it">There is (way) more to it</h2>

<p>Coupling can happen between two functions as in this example, but it can also happen at any level of the system: coupling between classes, between components, between the frontend and the backend, between the server code and the database, and the list goes on.</p>

<p>Low coupling is a central concept of software design and we will see many other ways to achieve that goal, for example by respecting <a href="/levels-of-abstraction">levels of abstraction</a>.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Coupling is when a piece of code knows something about another piece of code</li><li>Coupling makes the code difficult to change</li><li>Good design is about having low coupling</li><li>The farther apart the coupled pieces, the worse</li><li>If you can’t make coupling disappear, make the coupled pieces close from one another</li></ul>
</div>`

export default content
